import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import type {
  OfferPageFilterType,
  OfferPageRoom,
} from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  takeCurrentOfferState,
} from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isDateBetween } from 'lib/datetime/dateUtils'
import type { OfferCalendarSidebarDayCellFeatured, OfferCalendarSidebarDayCellType, OfferCalendarSidebarDayCellVariant } from './OfferCalendarSidebarDayCell'
import OfferCalendarSidebarDayCell from './OfferCalendarSidebarDayCell'
import ModalContext from 'contexts/ModalContext'
import OfferCalendarPickerRecommendationsModal from '../CalendarPicker/OfferCalendarPickerRecommendationsModal'
import { getSidebarCalendarMonthEmptyCells, getLocalDate } from 'lib/datetime/calendarUtils'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { EmptyArray } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import {
  getFixedFlashCalendarDayPricingState,
  getFixedFlashCalendarDayState,
  getFixedFlashCalendarSelectionMode,
} from '../CalendarPicker/fixedFlashLengthOfStayCalendarUtils'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

interface Props {
  days: Array<App.CalendarDay>
  cheapestPrice: number
  otherAvailability: { [checkIn: string]: boolean }
  selectedDay?: App.CalendarDay
  selectedCheckIn?: string
  selectedCheckOut?: string
  checkoutCalendarDays: Record<string, App.CalendarDay>
  checkInCalendarDays: Record<string, App.CalendarDay>
  maximumDuration?: number
  isFixedFlashLengthOfStayEnabled?: boolean
  isAnyFlightsActive: boolean
  currentOfferId?: string
  enableCalendarRecommendations?: boolean
  displayMemberPrice?: boolean
  rooms: Array<OfferPageRoom>
  activeHeaderFilter?: OfferPageFilterType
}

export function getSidebarCalendarDayCellType(
  dayCheckIn: string,
  selectedCheckIn: string | undefined,
  selectedDay: App.CalendarDay | undefined,
  selectedCheckOut: string | undefined,
  isFixedFlashLengthOfStayEnabled: boolean,
): OfferCalendarSidebarDayCellType {
  const checkIn = selectedCheckIn ?? selectedDay?.checkIn
  if (!checkIn) return 'none'

  const effectiveCheckOut = isFixedFlashLengthOfStayEnabled ? selectedCheckOut : selectedDay?.checkOut
  if (dayCheckIn === checkIn) return 'check-in'
  if (dayCheckIn === effectiveCheckOut) return 'check-out'
  if (effectiveCheckOut && isDateBetween(new Date(checkIn), new Date(effectiveCheckOut), new Date(dayCheckIn))) return 'between'

  return 'none'
}

interface SidebarCalendarDayCellVariantOptions {
  isMinimumStayCheckoutDisabled: boolean
  isMaximumStayDisabled: boolean
  isFixedLengthCombinationUnavailable: boolean
  isFixedFlashLengthOfStayEnabled: boolean
  type: OfferCalendarSidebarDayCellType
  available: boolean
  displayMemberPrice?: boolean
  hasMemberPricing: boolean
  price: number
  cheapestPrice: number
  isAnyFlightsActive: boolean
  isHotelOnlyAvailable: boolean
  hasOtherAvailability: boolean
  enableCalendarRecommendations?: boolean
}

export function getSidebarCalendarDayCellVariant(options: SidebarCalendarDayCellVariantOptions): OfferCalendarSidebarDayCellVariant {
  if (options.isMinimumStayCheckoutDisabled || options.isMaximumStayDisabled) {
    return 'unavailable'
  }
  if (options.isFixedLengthCombinationUnavailable) {
    return 'no-availability'
  }
  if (options.isFixedFlashLengthOfStayEnabled && options.type === 'check-in') {
    return 'none'
  }
  if (options.available) {
    if (options.displayMemberPrice) {
      return options.hasMemberPricing ? 'price-member' : 'price'
    }
    return options.price === options.cheapestPrice ? 'price-cheapest' : 'price'
  }
  if (options.isAnyFlightsActive && options.isHotelOnlyAvailable) {
    return 'sold-out-hotel'
  }
  if (options.hasOtherAvailability) {
    return 'sold-out-rooms'
  }
  if (options.enableCalendarRecommendations) {
    return 'sold-out-recommendations'
  }

  return 'unavailable'
}

export function OfferCalendarSidebarDaysMonth(props: Props) {
  const {
    days,
    cheapestPrice,
    enableCalendarRecommendations,
    otherAvailability,
    selectedDay,
    selectedCheckIn,
    selectedCheckOut,
    checkoutCalendarDays,
    checkInCalendarDays,
    maximumDuration,
    isFixedFlashLengthOfStayEnabled = false,
    isAnyFlightsActive,
    currentOfferId,
    displayMemberPrice,
    rooms,
    activeHeaderFilter,
  } = props

  const intl = useIntl()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const showNewMobileMultipleRoom = rooms.length > 1
  const modalContext = useModalElementContext()

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const [minimumStayTooltipDate, setMinimumStayTooltipDate] = useState<string>()
  const [maximumStayTooltipDate, setMaximumStayTooltipDate] = useState<string>()
  const minimumCheckoutDuration = useMemo(() => {
    if (!selectedCheckIn) return

    const durations = Object.keys(checkoutCalendarDays)
      .map(checkOut => moment(checkOut).diff(selectedCheckIn, 'days'))
      .filter(candidateDuration => candidateDuration > 0)

    return durations.length ? Math.min(...durations) : undefined
  }, [checkoutCalendarDays, selectedCheckIn])
  const minimumStayTooltipDescription = minimumCheckoutDuration === undefined ? undefined : intl.formatMessage(
    {
      id: '3O7r9dxCwhgC1g9Phllp',
      defaultMessage: '{nights, plural, one {# night minimum} other {# nights minimum}}',
      description: 'Tooltip shown when a checkout date is before the minimum stay requirement in the offer page calendar',
    },
    { nights: minimumCheckoutDuration },
  )
  const maximumStayTooltipDescription = maximumDuration === undefined ? undefined : intl.formatMessage(
    {
      id: 'PNKY6m2zJVzKiEVV559F',
      defaultMessage: '{nights, plural, one {# night maximum} other {# nights maximum}}',
      description: 'Tooltip shown when a check-in date is before the maximum stay requirement in the offer page calendar',
    },
    { nights: maximumDuration },
  )
  const clearFixedFlashTooltips = useCallback(() => {
    setMinimumStayTooltipDate(undefined)
    setMaximumStayTooltipDate(undefined)
  }, [])
  const fixedFlashSelectionMode = getFixedFlashCalendarSelectionMode({
    isFixedFlashLengthOfStayEnabled,
    selectedCheckIn,
    selectedCheckOut,
  })

  const onDayClick = useCallback((day: App.CalendarDay) => {
    const available = isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable
    const isBeforeSelectedCheckIn = !!selectedCheckIn && moment(day.checkIn).isBefore(selectedCheckIn, 'day')
    const isAfterSelectedCheckIn = !!selectedCheckIn && moment(day.checkIn).isAfter(selectedCheckIn, 'day')
    const fixedFlashState = getFixedFlashCalendarDayState({
      day,
      selectedCheckIn,
      selectedCheckOut,
      checkoutCalendarDays,
      checkInCalendarDays,
      isFixedFlashLengthOfStayDateSelection: fixedFlashSelectionMode === 'select-check-out',
      minimumCheckoutDuration,
      maximumDuration,
    })

    if (isFixedFlashLengthOfStayEnabled) {
      if (fixedFlashSelectionMode === 'selected-range') {
        clearFixedFlashTooltips()
        const selectedRangeCheckInPrice = displayMemberPrice && day.memberPricing ?
            (isAnyFlightsActive ? day.memberPricingWithFlights?.price : day.memberPricing.price) :
            (isAnyFlightsActive ? day.pricingWithFlights?.price : day.pricing.price)
        const canSelectSelectedRangeCheckIn = available && !!selectedRangeCheckInPrice && selectedRangeCheckInPrice > 0
        if (canSelectSelectedRangeCheckIn && day.checkIn !== selectedCheckIn) {
          dispatch({ type: OfferPageActions.setDates, checkIn: day.checkIn, checkOut: undefined })
          dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
          onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, day.checkIn)
          return
        }
        if (day.checkIn === selectedCheckOut) {
          dispatch({ type: OfferPageActions.setDates, checkIn: undefined, checkOut: undefined })
          dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: undefined })
          onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, day.checkIn)
          return
        }
        return
      }

      if (fixedFlashState.isMinimumStayCheckoutDisabled) {
        setMinimumStayTooltipDate(day.checkIn)
        setMaximumStayTooltipDate(undefined)
        return
      }
      if (fixedFlashState.isMaximumStayDisabled) {
        setMaximumStayTooltipDate(day.checkIn)
        setMinimumStayTooltipDate(undefined)
        return
      }
      clearFixedFlashTooltips()
      if (day.checkIn === selectedCheckIn) {
        return
      }
      if (fixedFlashState.isFixedLengthCombinationUnavailable) {
        return
      }
      if (fixedFlashSelectionMode === 'select-check-out' && selectedCheckIn && isAfterSelectedCheckIn && fixedFlashState.checkoutCalendarDay) {
        dispatch({ type: OfferPageActions.setDates, checkIn: selectedCheckIn, checkOut: day.checkIn })
        onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckOut, day.checkIn)
        return
      }
      if (fixedFlashSelectionMode === 'select-check-out' && selectedCheckIn && isBeforeSelectedCheckIn && available) {
        dispatch({ type: OfferPageActions.setDates, checkIn: day.checkIn, checkOut: undefined })
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
        onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, day.checkIn)
        return
      }
      if (fixedFlashSelectionMode === 'select-check-in' && available) {
        dispatch({ type: OfferPageActions.setDates, checkIn: day.checkIn, checkOut: undefined })
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
        onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, day.checkIn)
        return
      }
    }

    if (available) {
      clearFixedFlashTooltips()
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
    } else if (isAnyFlightsActive && !available && day.hotelAvailable) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    } else if (otherAvailability[day.checkIn]) {
      if (showNewMobileMultipleRoom) {
        if (activeHeaderFilter === 'checkin') {
          modalContext.resolve()
          dispatch({ type: OfferPageActions.closeHeaderFilter })
        }
        // Set dates so the selection option can see what is available even if not all rooms are
        dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
        dispatch({ type: OfferPageActions.toggleFilter, filter: 'multiRoomSelect' })
      } else {
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
      }
    } else if (enableCalendarRecommendations) {
      showModal(<OfferCalendarPickerRecommendationsModal checkIn={day.checkIn} />)
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, day.checkIn)
  }, [activeHeaderFilter, checkInCalendarDays, checkoutCalendarDays, clearFixedFlashTooltips, currentOfferId, dispatch, displayMemberPrice, enableCalendarRecommendations, fixedFlashSelectionMode, isAnyFlightsActive, isFixedFlashLengthOfStayEnabled, maximumDuration, minimumCheckoutDuration, modalContext, onUserAnalyticsAction, otherAvailability, selectedCheckIn, selectedCheckOut, showModal, showNewMobileMultipleRoom])

  const firstDate = days[0]
  // our month may not start on the first day of the month, because we can't check in
  // on those days (they may be in the past). So we fill in the earliest weeks cells
  const emptyCells = useMemo(() => getSidebarCalendarMonthEmptyCells(firstDate.checkIn), [firstDate])

  const startCellStyle = useMemo(() => {
    return {
      // we need to align the first day of the month with our day heading
      // e.g. if the first day of a month is a wednesday, it should start under wednesday!
      gridColumnStart: getLocalDate(firstDate.checkIn).getDay() === 0 ? 7 : getLocalDate(firstDate.checkIn).getDay(), // Sunday goes to the end of the week
    }
  }, [firstDate])

  return (
    <BaseGrid>
      {emptyCells.map((cell) => <OfferCalendarSidebarDayCell
        key={cell.day}
        day={cell as any}
        style={{ gridColumnStart: cell.column }}
        variant="unavailable"
        type="none"
      />)}
      {days.map((day, index) => {
        const fixedFlashState = getFixedFlashCalendarDayState({
          day,
          selectedCheckIn,
          selectedCheckOut,
          checkoutCalendarDays,
          checkInCalendarDays,
          isFixedFlashLengthOfStayDateSelection: fixedFlashSelectionMode === 'select-check-out',
          minimumCheckoutDuration,
          maximumDuration,
        })
        const resolvedCheckoutCalendarDay = fixedFlashSelectionMode === 'selected-range' && day.checkIn !== selectedCheckOut ? undefined : fixedFlashState.checkoutCalendarDay
        const resolvedCheckInCalendarDay = fixedFlashSelectionMode === 'selected-range' ? undefined : fixedFlashState.checkInCalendarDay
        const type = getSidebarCalendarDayCellType(day.checkIn, selectedCheckIn, selectedDay, selectedCheckOut, isFixedFlashLengthOfStayEnabled)
        const isBeforeSelectedCheckIn = !!selectedCheckIn && moment(day.checkIn).isBefore(selectedCheckIn, 'day')

        const { pricingDay, displayDuration } = getFixedFlashCalendarDayPricingState({
          day,
          isBeforeSelectedCheckIn,
          selectedCheckOut,
          checkoutCalendarDay: resolvedCheckoutCalendarDay,
          checkInCalendarDay: resolvedCheckInCalendarDay,
        })
        const isPriceWithFlights = isAnyFlightsActive && !!pricingDay.pricingWithFlights?.price
        let price = isPriceWithFlights ? pricingDay.pricingWithFlights!.price : pricingDay.pricing.price
        if (displayMemberPrice && pricingDay.memberPricing) {
          price = isPriceWithFlights ? pricingDay.memberPricingWithFlights!.price : pricingDay.memberPricing.price
        }
        const isCheapestFlightPrice = isAnyFlightsActive && price === cheapestPrice

        const baseAvailable = !!(isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable)
        const available = fixedFlashSelectionMode === 'selected-range' ? baseAvailable && price > 0 : baseAvailable
        const hasDisplayablePriceDay = !!(resolvedCheckoutCalendarDay || resolvedCheckInCalendarDay) || available
        const variant = getSidebarCalendarDayCellVariant({
          isMinimumStayCheckoutDisabled: fixedFlashState.isMinimumStayCheckoutDisabled,
          isMaximumStayDisabled: fixedFlashState.isMaximumStayDisabled,
          isFixedLengthCombinationUnavailable: fixedFlashState.isFixedLengthCombinationUnavailable,
          isFixedFlashLengthOfStayEnabled,
          type,
          available: hasDisplayablePriceDay,
          displayMemberPrice,
          hasMemberPricing: !!pricingDay.memberPricing,
          price,
          cheapestPrice,
          isAnyFlightsActive,
          isHotelOnlyAvailable: !available && day.hotelAvailable,
          hasOtherAvailability: !!otherAvailability[day.checkIn],
          enableCalendarRecommendations,
        })
        const showContentOnBetweenDay = type === 'between' && (!!resolvedCheckoutCalendarDay || available || variant === 'sold-out-recommendations')

        let featured: OfferCalendarSidebarDayCellFeatured | undefined = undefined
        if (available) {
          if (day.hasTactical || !!day.dealId || (day.hasFlightDeal && isAnyFlightsActive)) {
            featured = 'special'
          } else if ((!!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package) && canLoyaltyUpgrade) {
            featured = 'loyalty'
          }
        }

        return <OfferCalendarSidebarDayCell
          key={day.checkIn}
          style={emptyCells.length === 0 && index === 0 ? startCellStyle : undefined}
          day={day}
          flights={isAnyFlightsActive}
          type={type}
          variant={variant}
          price={price}
          duration={displayDuration}
          isCheapestFlightPrice={isCheapestFlightPrice}
          showContentOnBetweenDay={showContentOnBetweenDay}
          featured={featured}
          unselectable={fixedFlashState.isFixedLengthCombinationUnavailable || fixedFlashState.isMinimumStayCheckoutDisabled || fixedFlashState.isMaximumStayDisabled}
          minimumStayTooltipOpen={minimumStayTooltipDate === day.checkIn}
          minimumStayTooltipDescription={fixedFlashState.isMinimumStayCheckoutDisabled ? minimumStayTooltipDescription : undefined}
          maximumStayTooltipOpen={maximumStayTooltipDate === day.checkIn}
          maximumStayTooltipDescription={fixedFlashState.isMaximumStayDisabled ? maximumStayTooltipDescription : undefined}
          onClick={onDayClick}
        />
      })}
    </BaseGrid>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)
  return {
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(state.offer) : false,
    currentOfferId: state.paginator?.currentOfferId,
    isAnyFlightsActive: state.flights,
    displayMemberPrice: currentState.displayMemberPrice,
    rooms: currentState.rooms ?? EmptyArray,
    activeHeaderFilter: currentState.activeHeaderFilter,
  }
}, OfferCalendarSidebarDaysMonth)
